import React, { ReactNode } from 'react';
import { MessageBundle } from '@amzn/arb-tools';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import { DynamicText } from '@components/DynamicText';
import { Link, Text } from '@amzn/storm-ui';
import * as accountRegistrationConst from '@features/account-registration/constants';

/** Alert copy per service error code: a header key and a message key. */
export type ErrorAlertMapping = Record<
  string,
  { header: string; message: string }
>;

/**
 * Error codes the sponsored ads registration page explains to the user.
 * Codes not listed fall back to the general header and the internal
 * service error message.
 */
export const ERROR_CODE_TO_ALERT_MAPPING: ErrorAlertMapping = {
  INVALID_ADDRESS: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_INVALID_ADDRESS_ERROR_BANNER_MESSAGE_TRANSLATION_KEY,
  },
  ACCESS_DENIED_FOR_MANAGER_ACCOUNT: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_ACCESS_DENIED_FOR_MANAGER_ACCOUNT_TRANSLATION_KEY,
  },
  ADDRESS_BUSINESS_NAME_TOO_LONG: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_INVALID_BUSINESS_NAME_ERROR_BANNER_MESSAGE_TRANSLATION_KEY,
  },
  ACCOUNT_ALREADY_EXISTS_FOR_SELLING_ACCOUNT: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_ACCOUNT_EXISTS_ERROR_BANNER_MESSAGE_TRANSLATION_KEY,
  },
  ACCOUNT_ALREADY_EXISTS_FOR_VENDOR: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_ACCOUNT_ALREADY_EXISTS_FOR_VENDOR_TRANSLATION_KEY,
  },
  ACCOUNT_ALREADY_EXISTS_FOR_ACCOUNT_NAME: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_ACCOUNT_ALREADY_EXISTS_FOR_ACCOUNT_NAME_TRANSLATION_KEY,
  },
  ADDRESS_INVALID_STATE: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_ADDRESS_INVALID_STATE_TRANSLATION_KEY,
  },
  MISSING_COUNTRY_CODE: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_MISSING_COUNTRY_CODE_TRANSLATION_KEY,
  },
  MISSING_BUSINESS_NAME: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_MISSING_BUSINESS_NAME_TRANSLATION_KEY,
  },
  MISSING_ADDRESS_LINE_ONE: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_MISSING_ADDRESS_LINE_ONE_TRANSLATION_KEY,
  },
  MISSING_CITY: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: accountRegistrationConst.REGISTRATION_MISSING_CITY_TRANSLATION_KEY,
  },
  MISSING_ZIP_CODE: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_MISSING_ZIP_CODE_TRANSLATION_KEY,
  },
  MISSING_PHONE_NUMBER: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_MISSING_PHONE_NUMBER_TRANSLATION_KEY,
  },
  MISSING_STATE: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_MISSING_STATE_TRANSLATION_KEY,
  },
  MISSING_WEBSITE_URL: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_MISSING_WEBSITE_URL_TRANSLATION_KEY,
  },
  INVALID_STATE_OR_REGION: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_INVALID_STATE_OR_REGION_TRANSLATION_KEY,
  },
  INVALID_ZIP_CODE: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_INVALID_ZIP_CODE_TRANSLATION_KEY,
  },
  INVALID_WEBSITE_URL: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_INVALID_WEBSITE_URL_TRANSLATION_KEY,
  },
  DISPLAY_NAME_TOO_LONG: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_DISPLAY_NAME_TOO_LONG_TRANSLATION_KEY,
  },
  ACCOUNT_NAME_VALIDATION_BLOCKED: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_DISPLAY_NAME_NOT_ALLOWED_TRANSLATION_KEY,
  },
  INVALID_INPUT: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_INVALID_INPUT_ERROR_BANNER_MESSAGE_TRANSLATION_KEY,
  },
  INTERNAL_SERVICE_ERROR: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message:
      accountRegistrationConst.REGISTRATION_INTERNAL_SERVICE_ERROR_BANNER_MESSAGE_TRANSLATION_KEY,
  },
  PARTNER_ACCOUNT_INVALID_FOR_REGISTRATION: {
    header:
      accountRegistrationConst.PARTNER_ACCOUNT_INVALID_FOR_REGISTRATION_HEADER_TRANSLATION_KEY,
    message:
      accountRegistrationConst.PARTNER_ACCOUNT_INVALID_FOR_REGISTRATION_MESSAGE_TRANSLATION_KEY,
  },
  CONFLICT: {
    header:
      accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
    message: accountRegistrationConst.CONFLICTING_REQUEST_TRANSLATION_KEY,
  },
};

export const getErrorHeader = (
  intl: MessageBundle,
  errorCode: string,
  mapping: ErrorAlertMapping = ERROR_CODE_TO_ALERT_MAPPING,
): ReactNode => {
  const key = get(
    mapping,
    [errorCode, 'header'],
    accountRegistrationConst.REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
  );

  return intl.getMessage(key);
};

export const getErrorMessage = (
  intl: MessageBundle,
  errorCode: string,
  dspEntityId?: string,
  mapping: ErrorAlertMapping = ERROR_CODE_TO_ALERT_MAPPING,
): ReactNode => {
  const key = get(
    mapping,
    [errorCode, 'message'],
    accountRegistrationConst.REGISTRATION_INTERNAL_SERVICE_ERROR_BANNER_MESSAGE_TRANSLATION_KEY,
  );

  if (
    dspEntityId &&
    isEqual(errorCode, 'PARTNER_ACCOUNT_INVALID_FOR_REGISTRATION')
  ) {
    const linkConfig = {
      createDSPAdvertiserLink: `/dsp/${dspEntityId}/advertisers/new?legacy=true`,
    };

    return (
      <DynamicText
        message={intl.getMessage(key)}
        resolveComponent={(part) => (
          <Link href={linkConfig[part as keyof typeof linkConfig]}>
            {intl.getMessage(
              accountRegistrationConst.REGISTRATION_ACCOUNT_LINK_HERE_TEXT,
            )}
          </Link>
        )}
      />
    );
  }

  if (isEqual(errorCode, 'ACCOUNT_ALREADY_EXISTS_FOR_SELLING_ACCOUNT')) {
    return (
      <DynamicText
        message={intl.getMessage(key)}
        resolveComponent={() => (
          <Link href={accountRegistrationConst.ACCOUNT_OVERVIEW_REDIRECT_URL}>
            {intl.getMessage(
              accountRegistrationConst.REGISTRATION_ACCOUNT_LINK_HERE_TEXT,
            )}
          </Link>
        )}
      />
    );
  }

  if (isEqual(errorCode, 'ACCOUNT_ALREADY_EXISTS_FOR_VENDOR')) {
    const linkConfig = {
      accountOverviewLink:
        accountRegistrationConst.ACCOUNT_OVERVIEW_REDIRECT_URL,
      vendorRegistrationLink:
        accountRegistrationConst.LEGACY_VENDOR_REGISTRATION_REDIRECT_URL,
    };

    return (
      <DynamicText
        message={intl.getMessage(key)}
        resolveComponent={(part) => (
          <Link href={linkConfig[part as keyof typeof linkConfig]}>
            {intl.getMessage(
              accountRegistrationConst.REGISTRATION_ACCOUNT_LINK_HERE_TEXT,
            )}
          </Link>
        )}
      />
    );
  }

  return <Text>{intl.getMessage(key)}</Text>;
};

export const getErrorMessageText = (
  intl: MessageBundle,
  errorCode: string,
  mapping: ErrorAlertMapping = ERROR_CODE_TO_ALERT_MAPPING,
): string => {
  const key = get(
    mapping,
    [errorCode, 'message'],
    accountRegistrationConst.REGISTRATION_INTERNAL_SERVICE_ERROR_BANNER_MESSAGE_TRANSLATION_KEY,
  );
  return intl.getMessage(key);
};
